import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ShortUrl } from '@air/api';
import {
  addUploadingClipToContentCollectionAction,
  contentCollectionApplyAllSelector,
  contentCollectionCustomFieldsSelector,
  contentCollectionFormIdSelector,
} from '@air/feature-content-collection/redux';
import { type Upload, uploadByIdSelector } from '@air/redux-uploader';
import { callRequestWithRetry } from '@air/upload-utils';

import { getContentCollectionItemGroupName } from '~/components/ContentCollection/utils';
import { metadataForPublicUploadSelector } from '~/store/publicUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export const useFetchClipForContentCollectionUpload = () => {
  const dispatch = useDispatch();
  const store = useAirStore();

  const fetchClipForContentCollectionUpload = useCallback(
    async (uploadId: Upload['id']) => {
      const formId = contentCollectionFormIdSelector(store.getState());
      const upload = uploadByIdSelector(store.getState(), uploadId);
      const applyAll = contentCollectionApplyAllSelector(store.getState());
      const customFields = contentCollectionCustomFieldsSelector(store.getState());

      if (!upload) {
        return;
      }

      const uploadMetadata = metadataForPublicUploadSelector(store.getState(), uploadId);
      const shouldApplyCustomFields = !!formId && applyAll && customFields.length;
      const sessionId = uploadMetadata.sessionId;

      const { clip } = await callRequestWithRetry(() =>
        ShortUrl.getBoardClip(uploadMetadata.shortBoardId, upload.clipId),
      );

      if (shouldApplyCustomFields && sessionId && formId) {
        await callRequestWithRetry(() =>
          ShortUrl.batchUpdateFormAssets(uploadMetadata.shortBoardId, formId, {
            assetIds: [clip.assetId],
            formSessionId: sessionId,
            update: {
              customFields: customFields.map((customField) => {
                return {
                  customFieldId: customField.id,
                  valueId: customField.value?.id ?? undefined,
                  valueIdsToAdd: customField.values?.map(({ id }) => id) ?? undefined,
                  plainTextValue: customField.plainTextValue ?? undefined,
                  dateValue: customField.dateValue ?? undefined,
                };
              }),
            },
          }),
        );
      }

      dispatch(
        addUploadingClipToContentCollectionAction({
          clip: {
            ...(shouldApplyCustomFields
              ? {
                  ...clip,
                  customFields: customFields.map((customField) => ({
                    ...customField,
                    assetId: clip.assetId,
                  })),
                }
              : clip),
            uploadId,
            groupName: getContentCollectionItemGroupName(upload),
          },
        }),
      );

      return clip;
    },
    [dispatch, store],
  );

  return { fetchClipForContentCollectionUpload };
};
